import type { ResourceStrategyMap } from "./resource-map";

/**
 * The scopes a grant can carry, in the order the editor offers them. Nobody writes conditions
 * by hand: a grant names one of these, and `SubjectScopeMap` below says what that compiles to
 * for the subject at hand. The reach is chosen per grant rather than fixed per pair, because two
 * roles can legitimately hold the same action under different reach — org-wide `update Protocol`
 * against only-own-projects. Which of these a given pair may choose from is `ActionScopeMap` at
 * the foot of this file, and a pair whose answer there is one entry long is a reach nobody has to
 * decide rather than a grant anybody is given.
 */
export const PERMISSION_SCOPES = ["organization", "own", "ownProjects"] as const;

export type PermissionScope = (typeof PERMISSION_SCOPES)[number];

/**
 * A subject's offer: which scopes it supports, and the conditions template each one compiles
 * to. `null` is a real template — a grant with no conditions at all, for subjects whose backing
 * table carries no column a condition could bind (the gate then works at the category level and
 * row scoping is the database's job). A scope absent from the record is not offered.
 */
type SubjectScopes = Partial<Record<PermissionScope, Record<string, unknown> | null>>;

const ORG = { orgId: "${user.orgId}" };

/** `own` for records that are ABOUT the user — HR records, not authored artifacts. */
const ABOUT_ME = { orgId: "${user.orgId}", userId: "${user.id}" };

/** `own` for artifacts the user AUTHORED. Nullable column: a row whose creator was deleted stops matching, which is the correct direction. */
const CREATED_BY_ME = { orgId: "${user.orgId}", createdBy: "${user.id}" };

/**
 * Which scopes each subject offers, and what they compile to — the same question
 * `ResourceStrategyMap` answers for actions, in a second dimension. Every condition key is a
 * real column on the subject's backing table (the shape types in `types.ts` are the census); a
 * key the row does not carry evaluates false in CASL and locks the grant out silently, which is
 * why scopes are declared here once instead of assembled per call site.
 *
 * The `satisfies` clause forces an entry per manifest subject: a new subject does not compile
 * until it declares its scopes, so the editor can never render a subject it cannot write.
 */
export const SubjectScopeMap = {
	Stock: { organization: ORG },
	// Reaching somebody. The commercial halves below are their own subjects precisely so a role
	// can hold this one without them.
	Contact: { organization: ORG },
	ContactMembers: { organization: ORG },
	ContactProjects: { organization: ORG },
	ContactServices: { organization: ORG },
	Vehicle: { organization: ORG },
	Service: { organization: ORG },
	// organizationPartnerships has no orgId column — the host side is the organisation whose
	// view this is, so org-wide reach binds hostOrgId.
	Partner: { organization: { hostOrgId: "${user.orgId}" } },
	Team: { organization: ORG },
	Permission: { organization: ORG },
	Absence: { organization: ORG, own: ABOUT_ME },
	Attendance: { organization: ORG, own: ABOUT_ME },
	// The users row carries no org_id — membership lives in organization_members — so an orgId
	// condition here would fail closed. Org-wide User reach is a category-level grant.
	User: { organization: null },
	// Contract data. `own` is the employee's own sheet, which is what a member starts with;
	// reading a colleague's is the wider grant an organisation hands out through a role it
	// creates for personnel work, and the two have to stay tellable apart.
	Employment: { organization: ORG, own: ABOUT_ME },
	Invoice: { organization: ORG, own: CREATED_BY_ME },
	IncomingInvoice: { organization: ORG, own: CREATED_BY_ME },
	// ticket_assignment_measurement_statements carries `org_id` under the standard organisation
	// policy, so the database scopes the rows; Citadel gates Measurement at the category level
	// and offers no organisation template the policy would only duplicate.
	Measurement: { organization: null },
	Calculation: { organization: ORG, own: CREATED_BY_ME },
	Project: {
		organization: ORG,
		own: CREATED_BY_ME,
		// A project IS the row project membership points at, so "own projects" binds the row's
		// own id — unlike Ticket, where it binds the foreign key.
		ownProjects: { orgId: "${user.orgId}", id: { $in: "${user.projectIds}" } },
	},
	// A plan is the organisation's daily board, not an authored document — no createdBy by design.
	Plan: { organization: ORG },
	Organization: { organization: ORG },
	Note: { organization: ORG, own: CREATED_BY_ME },
	// Three reaches, and all three are used: everybody holds `own` by standing, a site manager may
	// hold `ownProjects` for the jobs they run, and the office may hold the lot. A call request
	// addressed to somebody else is none of these — `Protocols.getMyCallRequests` serves that, hand
	// scoped, because "written by me or addressed to me" is neither one condition nor one grant.
	Protocol: {
		organization: ORG,
		own: CREATED_BY_ME,
		ownProjects: { orgId: "${user.orgId}", projectId: { $in: "${user.projectIds}" } },
	},
	Integration: { organization: ORG },
	// One reach only. A wiki is shared knowledge — "the pages I wrote" is not a boundary
	// anybody wants, and the spaces carry a `visibility` column the server has never read.
	Wiki: { organization: ORG },
} as const satisfies Record<keyof typeof ResourceStrategyMap, SubjectScopes>;

/**
 * Whether reach `a` contains reach `b`. `organization` contains everything; each narrow scope
 * contains itself and "no access"; the two narrow scopes — own rows and own projects — are
 * different axes and contain neither each other nor anything but themselves. This is the
 * relation the individual grid's pre-selection disables options by: an option a role already
 * contains offers nothing.
 *
 * @param a - The containing reach.
 * @param b - The contained candidate; `false` is "no access", which everything contains.
 * @returns `true` when granting `b` adds nothing to someone who holds `a`.
 */
export function scopeContains(a: PermissionScope, b: false | PermissionScope): boolean {
	if (b === false || a === b) return true;
	return a === "organization";
}

/**
 * The scopes a subject offers, in the fixed `PERMISSION_SCOPES` order — what the editor renders
 * for one subject's cells.
 *
 * @param subject - The manifest subject.
 * @returns The offered scopes, always at least `organization`.
 */
export function getSubjectScopes(subject: keyof typeof SubjectScopeMap): PermissionScope[] {
	const scopes: SubjectScopes = SubjectScopeMap[subject];
	return PERMISSION_SCOPES.filter((scope) => scope in scopes);
}

/**
 * The conditions template a grant gets when it names a scope for a subject. This is the ONLY
 * producer of `rolePermissions.conditions` on the editor's write path — nobody writes condition
 * objects by hand.
 *
 * @param subject - The manifest subject.
 * @param scope - The chosen scope.
 * @returns The uninterpolated conditions template (`null` for a deliberate no-conditions
 *   grant), or `undefined` when the subject does not offer the scope.
 */
export function conditionsForScope(
	subject: keyof typeof SubjectScopeMap,
	scope: PermissionScope,
): Record<string, unknown> | null | undefined {
	const scopes: SubjectScopes = SubjectScopeMap[subject];
	if (!(scope in scopes)) return undefined;
	return scopes[scope] ?? null;
}

/**
 * Canonical serialisation for template comparison: object keys sorted recursively, so the same
 * template compares equal however it was stored — jsonb re-orders keys, authors do not.
 */
function canonical(value: unknown): string {
	if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
	if (value !== null && typeof value === "object") {
		const entries = Object.entries(value as Record<string, unknown>)
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([key, entry]) => `${JSON.stringify(key)}:${canonical(entry)}`);
		return `{${entries.join(",")}}`;
	}
	return JSON.stringify(value);
}

/**
 * The reverse lookup, for the editor's read path: which scope a stored conditions template
 * represents. A template this map did not produce — hand-written rows predating the editor,
 * say — answers `null`, and the editor must surface that rather than misrepresent the grant as
 * the nearest scope.
 *
 * @param subject - The manifest subject the grant is on.
 * @param conditions - The stored, uninterpolated conditions template (`null` for none).
 * @returns The scope the template encodes, or `null` when it is none of the subject's offers.
 */
export function scopeForConditions(
	subject: keyof typeof SubjectScopeMap,
	conditions: Record<string, unknown> | null,
): PermissionScope | null {
	const target = canonical(conditions ?? null);

	for (const scope of getSubjectScopes(subject)) {
		const template = conditionsForScope(subject, scope);
		if (canonical(template ?? null) === target) return scope;
	}

	return null;
}

/**
 * The reaches one pair offers. A subset of the subject's own, in the fixed `PERMISSION_SCOPES`
 * order, and never empty — a pair offering nothing could not be granted at all, which is what
 * leaving it out of `ResourceStrategyMap` says.
 */
type OfferedScopes<S extends keyof typeof SubjectScopeMap> = ReadonlyArray<
	keyof (typeof SubjectScopeMap)[S] & PermissionScope
>;

/** One offer per pair the manifest declares, and none the manifest does not. */
type ActionScopes = {
	[S in keyof typeof ResourceStrategyMap]: {
		[A in (typeof ResourceStrategyMap)[S]["actions"][number]]: OfferedScopes<S>;
	};
};

const ORG_ONLY = ["organization"] as const;
const ORG_OR_OWN = ["organization", "own"] as const;
const ORG_OWN_OR_PROJECTS = ["organization", "own", "ownProjects"] as const;
const OWN_ONLY = ["own"] as const;

/**
 * Which reaches each **pair** offers — the subject's offer narrowed by the action, where the two
 * differ.
 *
 * `SubjectScopeMap` above says what a reach compiles to, which is a question about columns and
 * therefore belongs to the subject. Which reaches make sense is a question about the action: a
 * person records their own absence and reads either their own or everybody's, and approving one
 * is meaningless narrowed to oneself. Most subjects offer every action the same set and say
 * so line by line, because the `satisfies` clause below requires an entry per declared
 * action: a new action on an existing subject does not compile until somebody decides what it
 * reaches, which is how `approve Attendance` would have arrived with an answer rather than with
 * the subject's default.
 *
 * **What the narrow entries are and are not.** Absence and Attendance offer `own` alone on
 * `create`, `update` and `delete`, so the editor cannot widen those three to the organisation by
 * a stray click. That is protection against a slip and nothing more: the grid still offers no
 * access, no rule is granted by standing anywhere, and an organisation that wants its members
 * unable to book their own hours can still arrange it. The state rules — whether a begun absence
 * or a signed-off week may still be edited — are `getIsAbsenceEditable` and
 * `getIsAttendanceEditable` in the handlers, and they are untouched by any of this.
 *
 * **A subject whose `own` binds `createdBy` offers `organization` alone on `create`.** The route
 * gate decides a create at the category level — it asks whether the caller holds any rule for the
 * pair and evaluates no conditions, since there is no row yet to hold them against — and the
 * database stamps `createdBy` from the claims on every one of these tables, so the row a create
 * produces is the caller's by construction. A grant narrowed to "only their own" on such a pair
 * is therefore the organisation-wide grant under a misleading name, and the editor does not offer
 * it. `own` stays on `create` where it binds `userId` (Absence, Attendance, Employment): there the
 * condition names a person, and filing a record about oneself is a real boundary. The test
 * "a createdBy subject offers the organisation reach alone on create" holds every subject to it.
 */
export const ActionScopeMap = {
	Stock: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Contact: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	ContactMembers: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	ContactProjects: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	ContactServices: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Vehicle: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Service: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Partner: {
		read: ORG_ONLY,
		create: ORG_ONLY,
		update: ORG_ONLY,
		delete: ORG_ONLY,
		approve: ORG_ONLY,
	},
	Team: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Permission: { manage: ORG_ONLY },
	// Filing, correcting and withdrawing are about one's own record; deciding about somebody
	// else's leave is `approve`, which narrowed to oneself would be self-approval.
	Absence: {
		read: ORG_OR_OWN,
		create: OWN_ONLY,
		update: OWN_ONLY,
		delete: OWN_ONLY,
		approve: ORG_ONLY,
	},
	// The same shape, for the same reason: booking and correcting hours are one's own, and
	// signing off a submitted week is the thing a team leader does to somebody else's.
	Attendance: {
		read: ORG_OR_OWN,
		create: OWN_ONLY,
		update: OWN_ONLY,
		delete: OWN_ONLY,
		approve: ORG_ONLY,
	},
	User: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Employment: { read: ORG_OR_OWN, create: ORG_OR_OWN, update: ORG_OR_OWN, delete: ORG_OR_OWN },
	Invoice: { read: ORG_OR_OWN, create: ORG_ONLY, update: ORG_OR_OWN, delete: ORG_OR_OWN },
	IncomingInvoice: {
		read: ORG_OR_OWN,
		create: ORG_ONLY,
		update: ORG_OR_OWN,
		delete: ORG_OR_OWN,
	},
	Measurement: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Calculation: {
		read: ORG_OR_OWN,
		create: ORG_ONLY,
		update: ORG_OR_OWN,
		delete: ORG_OR_OWN,
		approve: ORG_ONLY,
	},
	Project: {
		read: ORG_OWN_OR_PROJECTS,
		create: ORG_ONLY,
		update: ORG_OWN_OR_PROJECTS,
		delete: ORG_OWN_OR_PROJECTS,
	},
	Plan: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Organization: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Note: { read: ORG_OR_OWN, create: ORG_ONLY, update: ORG_OR_OWN, delete: ORG_OR_OWN },
	Protocol: {
		read: ORG_OWN_OR_PROJECTS,
		create: ORG_ONLY,
		update: ORG_OWN_OR_PROJECTS,
		delete: ORG_OWN_OR_PROJECTS,
	},
	Integration: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
	Wiki: { read: ORG_ONLY, create: ORG_ONLY, update: ORG_ONLY, delete: ORG_ONLY },
} as const satisfies ActionScopes;

/** A pair the manifest declares, as the grid and both write paths address it. */
export type GrantablePair = {
	[S in keyof typeof ActionScopeMap]: {
		subject: S;
		action: keyof (typeof ActionScopeMap)[S];
	};
}[keyof typeof ActionScopeMap];

/**
 * The reaches one pair offers, in the fixed `PERMISSION_SCOPES` order — what the editor renders
 * in one cell, and what both write paths accept for it.
 *
 * @param subject - The manifest subject.
 * @param action - An action the subject declares. Anything else answers an empty list, which is
 *   the same answer the manifest gives: the pair is not grantable.
 * @returns The offered reaches, narrowest offer first in `PERMISSION_SCOPES` order.
 */
export function getScopesFor(
	subject: keyof typeof ActionScopeMap,
	action: string,
): PermissionScope[] {
	const offers = ActionScopeMap[subject] as Record<string, readonly PermissionScope[] | undefined>;
	const offered = offers[action];
	if (!offered) return [];
	return PERMISSION_SCOPES.filter((scope) => offered.includes(scope));
}

/**
 * The conditions template a grant gets, refusing a reach the pair does not offer. This is what
 * both write paths validate against: the only question the server asks about a chosen reach is
 * whether the pair offers it.
 *
 * @param grant - The pair being granted.
 * @param scope - The chosen reach.
 * @returns The uninterpolated template (`null` for a deliberate no-conditions grant), or
 *   `undefined` when the pair does not offer the reach.
 */
export function conditionsForGrant(
	grant: GrantablePair,
	scope: PermissionScope,
): Record<string, unknown> | null | undefined {
	if (!getScopesFor(grant.subject, grant.action as string).includes(scope)) return undefined;
	return conditionsForScope(grant.subject, scope);
}

/**
 * The reach a stored grant represents, for the editor's read path — `scopeForConditions` with
 * the pair's offer applied.
 *
 * A row whose template is one the map never produced answers `null`, and so does one whose
 * reach the pair no longer offers. Both are the same situation for the editor: it cannot show
 * the grant as one of the cell's choices without misstating it, so it renders the cell read-only
 * and leaves the row out of what it sends.
 *
 * @param grant - The pair the stored row is on.
 * @param conditions - The stored, uninterpolated template (`null` for none).
 * @returns The reach the row encodes, or `null` when the cell cannot represent it.
 */
export function scopeForGrant(
	grant: GrantablePair,
	conditions: Record<string, unknown> | null,
): PermissionScope | null {
	const scope = scopeForConditions(grant.subject, conditions);
	if (!scope) return null;
	return getScopesFor(grant.subject, grant.action as string).includes(scope) ? scope : null;
}
